import { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Truck } from "lucide-react";

const STATUS_COLORS = {
  pending: "#64748B", design: "#6366F1", production: "#F59E0B", ready: "#0047AB", delivered: "#10B981"
};
const STATUS_LABELS = { pending: "Pendiente", design: "Diseño", production: "En producción", ready: "Listo", delivered: "Entregado" };

export default function AdminCalendar() {
  const [orders, setOrders] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/admin/calendar")
      .then((r) => { setOrders(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const deliveryDates = useMemo(() => {
    const dates = {};
    orders.forEach((o) => {
      if (o.delivery_date) {
        const d = o.delivery_date.split("T")[0];
        if (!dates[d]) dates[d] = [];
        dates[d].push(o);
      }
    });
    return dates;
  }, [orders]);

  const selectedDateStr = selectedDate.toISOString().split("T")[0];
  const selectedOrders = useMemo(() => deliveryDates[selectedDateStr] || [], [deliveryDates, selectedDateStr]);

  const calendarModifiers = useMemo(() => ({
    hasDelivery: Object.keys(deliveryDates).map((d) => new Date(d + "T12:00:00")),
  }), [deliveryDates]);

  const calendarModifiersStyles = useMemo(() => ({
    hasDelivery: { fontWeight: "bold", backgroundColor: "#EFF6FF", color: "#0047AB", borderRadius: "2px" },
  }), []);

  const upcomingOrders = useMemo(() => orders.slice(0, 8), [orders]);

  return (
    <div className="p-6" data-testid="admin-calendar-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Planificación</p>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Calendario de Entregas</h1>
      </div>

      {loading ? (
        <p className="text-sm text-[#94A3B8]">Cargando...</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-4">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                className="w-full"
                modifiers={calendarModifiers}
                modifiersStyles={calendarModifiersStyles}
                data-testid="delivery-calendar"
              />
            </div>
          </div>
          <div>
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-4">
              <h3 className="text-sm font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Chivo' }}>
                Entregas: {selectedDate.toLocaleDateString("es", { day: "numeric", month: "long" })}
              </h3>
              {selectedOrders.length === 0 ? (
                <p className="text-sm text-[#94A3B8] py-4">Sin entregas programadas</p>
              ) : (
                <div className="space-y-3">
                  {selectedOrders.map((o) => (
                    <div key={o.id} className="border border-[#E2E8F0] rounded-sm p-3" data-testid={`calendar-order-${o.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[o.status] }} />
                        <span className="text-xs font-bold text-[#0F172A]">{o.client_name}</span>
                      </div>
                      <p className="text-xs text-[#475569]">{o.items?.map((i) => i.product_name).join(", ")}</p>
                      <div className="flex justify-between mt-2 text-xs">
                        <span className="text-[#475569]">{STATUS_LABELS[o.status]}</span>
                        <span className="font-semibold text-[#0F172A]">Q{o.total}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-sm p-4 mt-4">
              <h3 className="text-sm font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Chivo' }}>Próximas Entregas</h3>
              {orders.length === 0 ? (
                <p className="text-sm text-[#94A3B8]">Sin entregas próximas</p>
              ) : (
                <div className="space-y-2">
                  {upcomingOrders.map((o) => (
                    <div key={o.id} className="flex items-center justify-between text-xs py-1">
                      <div className="flex items-center gap-2">
                        <Truck className="w-3 h-3 text-[#475569]" />
                        <span className="text-[#0F172A] font-medium">{o.client_name}</span>
                      </div>
                      <span className="text-[#475569]">{o.delivery_date?.split("T")[0]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
